import React, { useState, useEffect, useCallback } from 'react';
import { useApp } from '../context/AppContext';
import { FaUserCircle } from 'react-icons/fa';
import styled, { keyframes } from 'styled-components';

const slideInLeft = keyframes`
  from { transform: translateX(-100%); opacity: 0; }
  to { transform: translateX(0); opacity: 1; }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Item = styled.div`
  background: #28a745;
  color: white;
  padding: 12px 16px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  animation: ${slideInLeft} 0.3s ease-out;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  transition: box-shadow 0.2s;

  &:hover {
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
  }
`;

const AvatarWrap = styled.div`
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  overflow: hidden;
  background: rgba(255,255,255,0.25);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Body = styled.div`
  flex: 1;
  min-width: 0;
  font-size: 14px;
  line-height: 1.4;
`;

const Name = styled.div`
  font-weight: 700;
  margin-bottom: 4px;
`;

const Text = styled.div`
  opacity: 0.95;
  margin-bottom: 2px;
`;

const ActionsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 10px;
  flex-wrap: wrap;
`;

const ApproveBtn = styled.button`
  padding: 6px 14px;
  border-radius: 8px;
  border: none;
  background: #fff;
  color: #1a5f1a;
  font-weight: 700;
  font-size: 14px;
  cursor: pointer;
  transition: background 0.2s, transform 0.1s;

  &:hover {
    background: #e8f5e9;
    transform: scale(1.02);
  }
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;
  }
`;

const CloseBtn = styled.button`
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border: 2px solid #000;
  border-radius: 50%;
  background: #f18181;
  color: #000;
  cursor: pointer;
  font-size: 24px;
  font-weight: bold;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  transition: background 0.2s;

  &:hover {
    background: #f52f2f;
  }
`;

const LEAVE_TYPE_LABELS = {
  vacation: 'Отпуск',
  sick: 'Больничный',
  remote: 'Удаленка',
  leave: 'Отгул'
};

function formatDate(s) {
  if (!s) return '';
  const d = new Date(s);
  return d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function formatLeaveText(leave) {
  const typeLabel = LEAVE_TYPE_LABELS[leave.type] || leave.type || 'Заявка';
  const range = `${formatDate(leave.startDate)} – ${formatDate(leave.endDate)}`;
  return leave.reason ? `${typeLabel}, ${range}. Причина: ${leave.reason}` : `${typeLabel}, ${range}.`;
}

export default function LeaveRequestNotifications() {
  const { state } = useApp();
  const [pendingLeaves, setPendingLeaves] = useState([]);
  const [users, setUsers] = useState({});
  const [dismissedIds, setDismissedIds] = useState(() => new Set());
  const [approvingId, setApprovingId] = useState(null);

  const isHrOrAdmin = state.user && (state.user.role === 'hr' || state.user.role === 'admin');

  const fetchData = useCallback(() => {
    if (!isHrOrAdmin) return;
    const token = localStorage.getItem('token');
    if (!token) return;

    Promise.all([
      fetch('/api/all-leaves', { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()),
      fetch('/api/users', { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json())
    ]).then(([leavesData, usersData]) => {
      const leaves = Array.isArray(leavesData) ? leavesData : [];
      const arr = Array.isArray(usersData) ? usersData : (Array.isArray(usersData?.users) ? usersData.users : []);
      const usersMap = Object.fromEntries((arr || []).map(u => [u.id, u]));
      setUsers(usersMap);
      setPendingLeaves(leaves.filter(l => l.status === 'pending'));
    }).catch(() => {});
  }, [isHrOrAdmin]);

  useEffect(() => {
    if (!isHrOrAdmin) return;
    fetchData();
    const interval = setInterval(fetchData, 10000);
    const onRefresh = () => fetchData();
    window.addEventListener('show-all-leaves', onRefresh);
    return () => {
      clearInterval(interval);
      window.removeEventListener('show-all-leaves', onRefresh);
    };
  }, [isHrOrAdmin, fetchData]);

  const handleDismiss = (leaveId) => {
    setDismissedIds(prev => new Set([...prev, leaveId]));
  };

  const handleApprove = async (leaveId) => {
    const token = localStorage.getItem('token');
    if (!token) return;
    setApprovingId(leaveId);
    try {
      const res = await fetch(`/api/leaves/${leaveId}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ status: 'approved' })
      });
      if (res.ok) {
        handleDismiss(leaveId);
        fetchData();
        window.dispatchEvent(new Event('show-all-leaves'));
      }
    } finally {
      setApprovingId(null);
    }
  };

  if (!isHrOrAdmin || pendingLeaves.length === 0) return null;

  const toShow = pendingLeaves.filter(l => !dismissedIds.has(l.id));
  if (toShow.length === 0) return null;

  return (
    <Container>
      {toShow.map(leave => {
        const user = users[leave.userId];
        const name = user?.username || (user?.first_name || user?.last_name
          ? `${user.first_name || ''} ${user.last_name || ''}`.trim()
          : null) || `Сотрудник #${leave.userId}`;
        const avatarUrl = user?.avatar;

        return (
          <Item key={leave.id}>
            <AvatarWrap>
              {avatarUrl ? (
                <img src={avatarUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : (
                <FaUserCircle size={28} color="rgba(255,255,255,0.9)" />
              )}
            </AvatarWrap>
            <Body>
              <Name>{name}</Name>
              <Text>{formatLeaveText(leave)}</Text>
              <ActionsRow>
                <ApproveBtn
                  type="button"
                  disabled={approvingId === leave.id}
                  onClick={(e) => { e.stopPropagation(); handleApprove(leave.id); }}
                  title="Одобрить заявку"
                >
                  {approvingId === leave.id ? '…' : 'Одобрить'}
                </ApproveBtn>
              </ActionsRow>
            </Body>
            <CloseBtn
              type="button"
              onClick={(e) => { e.stopPropagation(); handleDismiss(leave.id); }}
              aria-label="Закрыть"
              title="Закрыть уведомление"
            >
              ×
            </CloseBtn>
          </Item>
        );
      })}
    </Container>
  );
}
